import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Navbar, Image, Button, Alert, Spinner, Card, Table, Badge, Form, Breadcrumb, Modal } from 'react-bootstrap';
import { useUserAuth } from '../context/UserAuthContext';
import { BlobServiceClient } from '@azure/storage-blob';
import { BlobItem } from '../models/BlobItem';
import { storageConfig } from '../config/authConfig';
import UserFileViewer from './UserFileViewer';

const UserDashboard: React.FC = () => {
  const { user, containers, isAuthenticated, isLoading, logout, refreshContainers, userAuthService } = useUserAuth();
  const navigate = useNavigate();
  
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [blobs, setBlobs] = useState<BlobItem[]>([]);
  const [loadingBlobs, setLoadingBlobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedBlob, setSelectedBlob] = useState<BlobItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentFolderPath, setCurrentFolderPath] = useState<string>('');
  const [showCreateFolderModal, setShowCreateFolderModal] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [selectedBlobs, setSelectedBlobs] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<boolean>(false);
  const [sortField, setSortField] = useState<'name' | 'size' | 'type' | 'modified'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Create new Shared Folder (container) — gated by user.canCreateContainers
  const [showCreateContainerModal, setShowCreateContainerModal] = useState<boolean>(false);
  const [newContainerName, setNewContainerName] = useState<string>('');
  const [creatingContainer, setCreatingContainer] = useState<boolean>(false);
  const [createContainerError, setCreateContainerError] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/user/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Select first container by default
  useEffect(() => {
    if (containers.length > 0 && !selectedContainer) {
      setSelectedContainer(containers[0].name);
    }
  }, [containers, selectedContainer]);

  // Load blobs when container is selected
  useEffect(() => {
    if (selectedContainer) {
      setCurrentFolderPath(''); // Reset folder path when container changes
      loadBlobs(selectedContainer, '');
      setSelectedBlobs(new Set()); // Clear selections when container changes
    }
  }, [selectedContainer]);

  // Reload blobs when folder path changes
  useEffect(() => {
    if (selectedContainer) {
      loadBlobs(selectedContainer, currentFolderPath);
    }
  }, [currentFolderPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selections when folder path changes
  useEffect(() => {
    setSelectedBlobs(new Set());
  }, [currentFolderPath]);

  const loadBlobs = async (containerName: string, folderPath: string = '') => {
    setLoadingBlobs(true);
    setError(null);
    
    try {
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        console.log('No auth token, skipping blob list');
        setBlobs([]);
        setLoadingBlobs(false);
        return;
      }
      
      // Get SAS token for reading
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: containerName,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        let detail = '';
        try {
          detail = (await sasResponse.json())?.error || '';
        } catch { /* ignore */ }
        console.error('Cannot generate SAS token for reading:', sasResponse.status, detail);
        // Account disabled mid-session (or token no longer valid) → sign out cleanly.
        if (sasResponse.status === 401 || (sasResponse.status === 403 && /disabled/i.test(detail))) {
          logout();
          return;
        }
        setBlobs([]);
        setError(
          sasResponse.status === 403
            ? 'You do not have access to this shared folder.'
            : `Could not open this shared folder: ${detail || `error ${sasResponse.status}`}`
        );
        setLoadingBlobs(false);
        return;
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Use SAS token to list blobs
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${containerName}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      const blobList: BlobItem[] = [];
      try {
        // Use hierarchical listing to get folders and files
        const prefix = folderPath ? (folderPath.endsWith('/') ? folderPath : folderPath + '/') : '';
        
        for await (const item of containerClient.listBlobsByHierarchy('/', { prefix })) {
          if (item.kind === 'prefix') {
            // This is a folder/directory
            const folderName = item.name.replace(prefix, '').replace(/\/$/, '');
            blobList.push({
              name: folderName,
              contentLength: 0,
              contentType: 'application/x-directory',
              lastModified: new Date(),
              isFolder: true,
              path: item.name
            });
          } else {
            // This is a file
            const blobClient = containerClient.getBlobClient(item.name);
            const fileName = item.name.replace(prefix, '');
            
            // Skip directory marker blobs
            if (fileName.endsWith('.dir') || (item.properties.contentLength === 0 && item.properties.contentType === 'application/x-directory')) {
              continue;
            }
            
            blobList.push({
              name: fileName,
              contentLength: item.properties.contentLength || 0,
              contentType: item.properties.contentType || 'unknown',
              lastModified: item.properties.lastModified || new Date(),
              url: blobClient.url,
              isFolder: false,
              path: item.name
            });
          }
        }
        setBlobs(blobList);
      } catch (listError: any) {
        console.error('Cannot list blobs:', listError);
        setBlobs([]);
        const msg: string = listError?.message || '';
        const status: number | undefined = listError?.statusCode || listError?.status;
        if (status === 403 || /authorization|permission/i.test(msg)) {
          setError('You do not have permission to view files in this shared folder.');
        } else if (status === 404 || /does not exist|not found|ContainerNotFound/i.test(msg)) {
          setError(`Cannot open "${containerName}": that shared folder was not found in storage. Its name may be mis-cased (Azure container names are lowercase) or it may have been deleted.`);
        } else {
          setError(`Failed to load files: ${msg || 'Unknown error'}`);
        }
      }
    } catch (err: any) {
      console.error('Error loading blobs:', err);
      setBlobs([]);
      setError(`Failed to load files: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoadingBlobs(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      handleFilesAdded(filesArray);
    }
  };

  const handleFilesAdded = (files: File[]) => {
    setSelectedFiles(files);
    setError(null);
    setSuccess(null);
    // Upload immediately on selection — no separate "Upload" step.
    handleUpload(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const container = containers.find(c => c.name === selectedContainer);
    if (!container || !container.permissions.includes('write')) {
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      handleFilesAdded(filesArray);
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const triggerFolderInput = () => {
    const folderInput = document.getElementById('folderInput') as HTMLInputElement;
    if (folderInput) {
      folderInput.click();
    }
  };

  const handleUpload = async (filesArg?: File[]) => {
    const files = filesArg ?? selectedFiles;
    if (files.length === 0 || !selectedContainer) return;

    const container = containers.find(c => c.name === selectedContainer);
    if (!container) {
      setError('Shared folder not found');
      return;
    }
    
    if (!container.permissions.includes('write')) {
      setError('You do not have write permission for this shared folder');
      return;
    }
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress({});
    
    const uploadedFiles: string[] = [];
    const failedFiles: { name: string; error: string }[] = [];
    
    try {
      // Get user's JWT token from localStorage
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Call backend to generate SAS token for this container
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: selectedContainer,
          expiresInHours: 1 // Short-lived token for security
        })
      });
      
      if (!sasResponse.ok) {
        const errorData = await sasResponse.json().catch(() => ({ error: 'Failed to generate upload token' }));
        throw new Error(errorData.error || 'Failed to authenticate upload');
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Create blob service client with SAS token
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${selectedContainer}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // Upload all files
      for (const file of files) {
        try {
          // Preserve folder structure for folder uploads (webkitRelativePath),
          // otherwise just the file name.
          const relPath = (file as any).webkitRelativePath || file.name;
          const blobPath = currentFolderPath
            ? (currentFolderPath.endsWith('/') ? currentFolderPath + relPath : currentFolderPath + '/' + relPath)
            : relPath;

          const blockBlobClient = containerClient.getBlockBlobClient(blobPath);
          
          await blockBlobClient.uploadData(await file.arrayBuffer(), {
            blobHTTPHeaders: {
              blobContentType: file.type
            },
            onProgress: (progress) => {
              if (progress.loadedBytes && file.size) {
                const percent = Math.round((progress.loadedBytes / file.size) * 100);
                setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
              }
            }
          });
          
          uploadedFiles.push(file.name);
        } catch (err: any) {
          console.error(`Error uploading ${file.name}:`, err);
          failedFiles.push({ name: file.name, error: err.message || 'Unknown error' });
        }
      }
      
      // Set success/error messages
      if (uploadedFiles.length > 0) {
        const message = uploadedFiles.length === 1 
          ? `File "${uploadedFiles[0]}" uploaded successfully!`
          : `${uploadedFiles.length} file(s) uploaded successfully!`;
        setSuccess(message);
      }
      
      if (failedFiles.length > 0) {
        const message = failedFiles.length === 1
          ? `Failed to upload "${failedFiles[0].name}": ${failedFiles[0].error}`
          : `Failed to upload ${failedFiles.length} file(s)`;
        setError(message);
      }
      
      // Clear selections
      setSelectedFiles([]);
      setUploadProgress({});
      
      // Clear file input
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Reload blobs in the CURRENT folder (not root) so an upload inside a
      // folder doesn't briefly render that folder nested inside itself.
      await loadBlobs(selectedContainer, currentFolderPath);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (blob: BlobItem) => {
    try {
      if (!selectedContainer) return;
      
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Get SAS token for downloading
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: selectedContainer,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        throw new Error('Failed to authenticate download');
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Use SAS token to download
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${selectedContainer}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // Construct the full blob path
      const blobPath = blob.path || (currentFolderPath 
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + blob.name : currentFolderPath + '/' + blob.name)
        : blob.name);
      
      const blobClient = containerClient.getBlobClient(blobPath);
      
      const downloadResponse = await blobClient.download();
      let dataBlob: Blob | null = null;

      if (downloadResponse.blobBody) {
        dataBlob = await downloadResponse.blobBody;
      } else if (downloadResponse.readableStreamBody) {
        const webStream = downloadResponse.readableStreamBody as unknown as ReadableStream;
        const resp = new Response(webStream as any);
        dataBlob = await resp.blob();
      }

      if (!dataBlob) {
        throw new Error('Failed to download file');
      }

      const blobUrl = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = blob.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message || 'Unknown error'}`);
    }
  };

  const handleToggleSelect = (blobPath: string): void => {
    setSelectedBlobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blobPath)) {
        newSet.delete(blobPath);
      } else {
        newSet.add(blobPath);
      }
      return newSet;
    });
  };

  const handleSelectAll = (): void => {
    // This will be properly defined after filteredFiles is available
  };

  const handleDownloadSelected = async (): Promise<void> => {
    if (!selectedContainer || selectedBlobs.size === 0) return;
    
    setDownloading(true);
    setError(null);
    
    try {
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Get SAS token for downloading
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: selectedContainer,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        throw new Error('Failed to authenticate download');
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Use SAS token to download
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${selectedContainer}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // Get selected files from blobs (files are already separated)
      const selectedFiles = blobs.filter(blob => {
        if (blob.isFolder) return false;
        const path = blob.path || blob.name;
        return selectedBlobs.has(path);
      });

      // Download files sequentially to avoid overwhelming the browser
      for (const file of selectedFiles) {
        try {
          // Construct the full blob path
          const blobPath = file.path || (currentFolderPath 
            ? (currentFolderPath.endsWith('/') ? currentFolderPath + file.name : currentFolderPath + '/' + file.name)
            : file.name);
          
          const blobClient = containerClient.getBlobClient(blobPath);
          const downloadResponse = await blobClient.download();
          
          let dataBlob: Blob | null = null;
          if (downloadResponse.blobBody) {
            dataBlob = await downloadResponse.blobBody;
          } else if (downloadResponse.readableStreamBody) {
            const webStream = downloadResponse.readableStreamBody as unknown as ReadableStream;
            const resp = new Response(webStream as any);
            dataBlob = await resp.blob();
          }

          if (dataBlob) {
            const blobUrl = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }
          
          // Small delay between downloads to prevent browser blocking
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          console.error(`Error downloading ${file.name}:`, err);
          // Continue with other files even if one fails
        }
      }
      
      setSelectedBlobs(new Set());
    } catch (err: any) {
      console.error('Download error:', err);
      setError(`Failed to download one or more files: ${err.message || 'Unknown error'}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteSelected = async (): Promise<void> => {
    if (!selectedContainer || selectedBlobs.size === 0) return;
    const container = containers.find(c => c.name === selectedContainer);
    if (!container || !container.permissions.includes('write')) {
      setError('You do not have permission to delete from this shared folder');
      return;
    }
    const count = selectedBlobs.size;
    if (!window.confirm(`Delete ${count} selected item(s)? This action cannot be undone.`)) return;

    // Optimistic removal so it feels instant; restore on hard failure.
    const prevBlobs = blobs;
    const toDelete = blobs.filter(b => !b.isFolder && selectedBlobs.has(b.path || b.name));
    setBlobs(prev => prev.filter(b => !selectedBlobs.has(b.path || b.name)));
    setSelectedBlobs(new Set());

    try {
      const token = localStorage.getItem('userAuthToken');
      if (!token) throw new Error('Authentication required. Please log in again.');

      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ containerName: selectedContainer, expiresInHours: 1 })
      });
      if (!sasResponse.ok) {
        if (sasResponse.status === 401) throw new Error('Your session has expired. Please log out and log back in.');
        throw new Error(`Could not authorize delete (error ${sasResponse.status}).`);
      }
      const { sasToken } = await sasResponse.json();

      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const blobServiceClient = new BlobServiceClient(`${accountUrl}/${selectedContainer}?${sasToken}`);
      const containerClient = blobServiceClient.getContainerClient('');

      let failed = 0;
      for (const file of toDelete) {
        try {
          const blobPath = file.path || (currentFolderPath
            ? (currentFolderPath.endsWith('/') ? currentFolderPath + file.name : currentFolderPath + '/' + file.name)
            : file.name);
          await containerClient.getBlobClient(blobPath).delete();
        } catch (e) {
          failed++;
          console.error(`Error deleting ${file.name}:`, e);
        }
      }

      if (failed > 0) {
        setError(`${failed} of ${count} item(s) could not be deleted.`);
        await loadBlobs(selectedContainer, currentFolderPath);
      } else {
        setSuccess(`Deleted ${count} item(s)`);
        setTimeout(() => setSuccess(null), 4000);
      }
    } catch (err: any) {
      console.error('Bulk delete error:', err);
      setBlobs(prevBlobs);
      setError(`Delete failed: ${err.message || 'Unknown error'}`);
    }
  };

  const handleViewFile = (blob: BlobItem) => {
    if (blob.isFolder) {
      // Navigate into folder
      const newPath = currentFolderPath
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + blob.name : currentFolderPath + '/' + blob.name)
        : blob.name;
      setCurrentFolderPath(newPath);
    } else {
      setSelectedBlob(blob);
      setShowFileViewer(true);
    }
  };

  // Open a file in a new browser tab (native full-screen viewing for PDFs,
  // images, HTML, etc.). Opens the tab synchronously to avoid popup blockers,
  // then points it at a freshly-generated SAS URL for the blob.
  const handleOpenInNewTab = async (blob: BlobItem) => {
    if (!selectedContainer) return;
    const tab = window.open('', '_blank');
    try {
      const token = localStorage.getItem('userAuthToken');
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResp = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ containerName: selectedContainer, expiresInHours: 1 })
      });
      if (!sasResp.ok) throw new Error('Could not authorize file access');
      const { sasToken } = await sasResp.json();
      const path = blob.path || (currentFolderPath
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + blob.name : currentFolderPath + '/' + blob.name)
        : blob.name);
      const encoded = path.split('/').map(encodeURIComponent).join('/');
      const url = `https://${storageConfig.accountName}.blob.core.windows.net/${selectedContainer}/${encoded}?${sasToken}`;
      if (tab) tab.location.href = url; else window.open(url, '_blank');
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (e) {
      if (tab) tab.close();
      console.error('Open in new tab failed:', e);
      setError('Could not open the file in a new tab. Please try again.');
    }
  };

  const handleDelete = async (blob: BlobItem) => {
    if (!selectedContainer) return;

    const itemType = blob.isFolder ? 'folder' : 'file';
    const container = containers.find(c => c.name === selectedContainer);
    if (!container) {
      setError('Shared folder not found');
      return;
    }
    if (!container.permissions.includes('write')) {
      setError('You do not have permission to delete files from this shared folder');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete the ${itemType} "${blob.name}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    // Optimistic UI: drop it from the list immediately so deletion feels instant.
    const blobKey = blob.path || blob.name;
    const prevBlobs = blobs;
    setBlobs(prev => prev.filter(b => (b.path || b.name) !== blobKey));

    try {
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Get SAS token for deleting
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: selectedContainer,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        if (sasResponse.status === 401) {
          throw new Error('Your session has expired. Please log out and log back in.');
        }
        const detail = await sasResponse.json().catch(() => ({} as any));
        throw new Error(detail.error || `Could not authorize delete (error ${sasResponse.status}).`);
      }

      const { sasToken } = await sasResponse.json();

      // Use SAS token to delete blob
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${selectedContainer}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // Construct the full blob path
      const blobPath = blob.path || (currentFolderPath
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + blob.name : currentFolderPath + '/' + blob.name)
        : blob.name);

      if (blob.isFolder) {
        // The account uses a hierarchical namespace, so a folder is a real
        // directory. Recursively delete it via the Data Lake (dfs) REST endpoint
        // (the same container SAS works on the dfs endpoint for HNS accounts).
        const dirPath = blobPath.replace(/\/$/, '');
        const encodedPath = dirPath.split('/').map(encodeURIComponent).join('/');
        const dfsUrl = `https://${storageConfig.accountName}.dfs.core.windows.net/${selectedContainer}/${encodedPath}?recursive=true&${sasToken}`;
        const resp = await fetch(dfsUrl, { method: 'DELETE' });
        if (!resp.ok && resp.status !== 404) {
          throw new Error(`Could not delete folder (error ${resp.status}).`);
        }
      } else {
        await containerClient.getBlobClient(blobPath).delete();
      }

      setSuccess(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${blob.name}" deleted successfully`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Delete error:', err);
      // Restore the optimistically-removed item since the delete failed.
      setBlobs(prevBlobs);
      setError(`Delete failed: ${err.message || 'Unknown error'}`);
    }
  };

  const handleCreateFolder = async (): Promise<void> => {
    if (!selectedContainer || !newFolderName.trim()) return;
    
    const container = containers.find(c => c.name === selectedContainer);
    if (!container || !container.permissions.includes('write')) {
      setError('You do not have permission to create folders in this shared folder');
      return;
    }
    
    setCreatingFolder(true);
    setError(null);
    
    try {
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Get SAS token for creating folder
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: selectedContainer,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        throw new Error('Failed to authenticate folder creation');
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Use SAS token to create folder
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${selectedContainer}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // Construct the full folder path
      const fullFolderPath = currentFolderPath 
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + newFolderName.trim() : currentFolderPath + '/' + newFolderName.trim())
        : newFolderName.trim();
      
      // Ensure folder path ends with /
      const normalizedPath = fullFolderPath.endsWith('/') ? fullFolderPath : fullFolderPath + '/';
      
      // Create a zero-length blob with the folder path (folder marker)
      const blobClient = containerClient.getBlobClient(normalizedPath);
      const blockBlobClient = blobClient.getBlockBlobClient();
      
      await blockBlobClient.upload('', 0, {
        metadata: {
          hdi_isfolder: 'true'
        },
        blobHTTPHeaders: {
          blobContentType: 'application/x-directory'
        }
      });
      
      setShowCreateFolderModal(false);
      setNewFolderName('');
      await loadBlobs(selectedContainer, currentFolderPath);
      setSuccess(`Folder "${newFolderName.trim()}" created successfully`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error creating folder:', err);
      setError(`Failed to create folder: ${err.message || 'Unknown error'}`);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleBreadcrumbClick = (path: string): void => {
    setCurrentFolderPath(path);
  };

  // Client-side mirror of the server's container-name validation.
  const validateContainerName = (raw: string): string | null => {
    const name = raw.toLowerCase().trim();
    if (name.length < 3 || name.length > 63) {
      return 'Name must be 3–63 characters long.';
    }
    if (!/^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])$/.test(name)) {
      return 'Use lowercase letters, numbers and hyphens; start and end with a letter or number.';
    }
    if (name.includes('--')) {
      return 'Name cannot contain consecutive hyphens.';
    }
    return null;
  };

  const handleCreateContainer = async (): Promise<void> => {
    if (!userAuthService) return;
    const name = newContainerName.toLowerCase().trim();
    const validationError = validateContainerName(name);
    if (validationError) {
      setCreateContainerError(validationError);
      return;
    }

    setCreatingContainer(true);
    setCreateContainerError(null);
    try {
      await userAuthService.createContainer(name);
      setShowCreateContainerModal(false);
      setNewContainerName('');
      await refreshContainers();
      setSelectedContainer(name);
      setSuccess(`Shared Folder "${name}" created successfully`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error creating shared folder:', err);
      setCreateContainerError(err.message || 'Failed to create Shared Folder');
    } finally {
      setCreatingContainer(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getShortFileType = (contentType: string): string => {
    // Map common MIME types to short names
    const typeMap: { [key: string]: string } = {
      'application/pdf': 'PDF',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/zip': 'ZIP',
      'application/x-zip-compressed': 'ZIP',
      'application/json': 'JSON',
      'application/xml': 'XML',
      'text/plain': 'TXT',
      'text/csv': 'CSV',
      'text/html': 'HTML',
      'text/css': 'CSS',
      'text/javascript': 'JS',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/svg+xml': 'SVG',
      'image/webp': 'WEBP',
      'video/mp4': 'MP4',
      'video/mpeg': 'MPEG',
      'audio/mpeg': 'MP3',
      'audio/wav': 'WAV',
    };

    // Return mapped type if exists
    if (typeMap[contentType]) {
      return typeMap[contentType];
    }

    // Extract file extension from MIME type
    if (contentType.includes('/')) {
      const subtype = contentType.split('/')[1];
      // Handle vnd. prefixes and other complex types
      if (subtype.includes('.')) {
        const parts = subtype.split('.');
        return parts[parts.length - 1].toUpperCase().substring(0, 8);
      }
      return subtype.toUpperCase().substring(0, 8);
    }

    return contentType.toUpperCase().substring(0, 8);
  };

  const getPermissionBadge = (permissions: string) => {
    if (permissions === 'readwrite') return <Badge bg="success">Read & Write</Badge>;
    if (permissions === 'write') return <Badge bg="warning">Write Only</Badge>;
    return <Badge bg="info">Read Only</Badge>;
  };

  // Separate folders and files
  const folders = blobs.filter(blob => blob.isFolder);
  const files = blobs.filter(blob => !blob.isFolder);

  // Filter blobs based on search term
  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.contentType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort the file list by the chosen column (folders stay grouped first).
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'name') cmp = a.name.localeCompare(b.name);
    else if (sortField === 'size') cmp = (a.contentLength || 0) - (b.contentLength || 0);
    else if (sortField === 'type') cmp = (a.contentType || '').localeCompare(b.contentType || '');
    else cmp = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field: 'name' | 'size' | 'type' | 'modified'): void => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'modified' || field === 'size' ? 'desc' : 'asc');
    }
  };

  const sortIcon = (field: 'name' | 'size' | 'type' | 'modified'): string => {
    if (sortField !== field) return 'bi-arrow-down-up text-muted';
    return sortDir === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-up-alt';
  };

  const formatDateTime = (d: Date): string => {
    const dt = new Date(d);
    return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Update handleSelectAll to use filteredFiles (defined here)
  const handleSelectAllUpdated = (): void => {
    const allFilePaths = filteredFiles.map(file => file.path || file.name);
    if (selectedBlobs.size === allFilePaths.length && allFilePaths.every(path => selectedBlobs.has(path))) {
      // Deselect all
      setSelectedBlobs(new Set());
    } else {
      // Select all files (not folders)
      setSelectedBlobs(new Set(allFilePaths));
    }
  };

  // Build breadcrumb path
  const breadcrumbPaths = currentFolderPath ? currentFolderPath.split('/').filter(p => p) : [];
  const getBreadcrumbPath = (index: number): string => {
    return breadcrumbPaths.slice(0, index + 1).join('/');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // Safety check - if not authenticated or no user, don't render
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <Navbar bg="white" variant="light" className="mb-4 border-bottom shadow-sm">
        <Container>
          <Navbar.Brand>
            <Image
              src="/qualrisk-logo.webp"
              alt="QualRisk AI"
              height="36"
              width="200"
              className="d-inline-block align-top"
            />
          </Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end">
            <Navbar.Text className="me-3 text-muted">
              <i className="bi bi-person-circle me-2"></i>
              <span className="text-dark fw-medium">{user.displayName}</span>
            </Navbar.Text>
            <Button variant="outline-primary" size="sm" onClick={logout}>
              <i className="bi bi-box-arrow-right me-1"></i>
              Logout
            </Button>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container className="flex-grow-1 mb-4">
        <div className="mb-4">
          <h3>
            <i className="bi bi-speedometer2 me-2"></i>
            My Dashboard
          </h3>
          <p className="text-muted">Welcome back {user.displayName}!</p>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            <i className="bi bi-check-circle-fill me-2"></i>
            {success}
          </Alert>
        )}

        {containers.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <i className="bi bi-folder-x" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              <h4 className="mt-3">No Shared Folders Assigned</h4>
              <p className="text-muted">
                You don't have access to any shared folders yet.
                <br />
                Please reach out to your QualRisk AI Account Manager to assign you a shared folder.
              </p>
              {user?.canCreateContainers && (
                <Button
                  variant="primary"
                  onClick={() => { setCreateContainerError(null); setShowCreateContainerModal(true); }}
                >
                  <i className="bi bi-folder-plus me-2"></i>
                  New Shared Folder
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : (
          <div className="row">
            {/* Sidebar - Container List */}
            <div className="col-md-4 mb-4">
              <Card>
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bi bi-folder2-open me-2"></i>
                    My Shared Folders
                  </span>
                  {user?.canCreateContainers && (
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => { setCreateContainerError(null); setShowCreateContainerModal(true); }}
                      title="Create a new Shared Folder"
                    >
                      <i className="bi bi-folder-plus"></i>
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {containers.map((container) => (
                    <div
                      key={container.name}
                      className={`p-3 border-bottom cursor-pointer ${selectedContainer === container.name ? 'bg-light' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedContainer(container.name)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <i className="bi bi-folder me-2"></i>
                          <strong>{container.name}</strong>
                        </div>
                        {getPermissionBadge(container.permissions)}
                      </div>
                      <small className="text-muted">
                        Assigned: {new Date(container.assignedAt).toLocaleDateString()}
                      </small>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </div>

            {/* Main Panel - Files */}
            <div className="col-md-8">
              {selectedContainer ? (
                <>
                  {/* Hidden file input */}
                  <input
                    type="file"
                    multiple
                    id="fileInput"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                  {/* Hidden folder input (uploads a whole folder, preserving structure) */}
                  <input
                    type="file"
                    multiple
                    id="folderInput"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={uploading}
                    ref={(node) => { if (node) { node.setAttribute('webkitdirectory', ''); node.setAttribute('directory', ''); } }}
                  />

                  {/* Upload status and selected files */}
                  {selectedFiles.length > 0 && (
                    <Card className="mb-3 upload-preview-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <i className="bi bi-cloud-arrow-up text-primary me-2"></i>
                            <strong>{uploading ? `Uploading ${selectedFiles.length} file(s)…` : `${selectedFiles.length} file(s)`}</strong>
                          </div>
                          {uploading && (
                            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                          )}
                        </div>
                        <div className="selected-files-list">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="selected-file-item">
                              <i className="bi bi-file-earmark-text text-muted me-2"></i>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size text-muted ms-2">({formatFileSize(file.size)})</span>
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  <Card>
                    <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-folder me-2 text-primary"></i>
                        <h5 className="mb-0">{selectedContainer}</h5>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {selectedBlobs.size > 0 && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleDownloadSelected}
                            disabled={downloading}
                          >
                            <i className="bi bi-download me-2"></i>
                            {downloading ? `Downloading ${selectedBlobs.size}...` : `Download Selected (${selectedBlobs.size})`}
                          </Button>
                        )}
                        {selectedBlobs.size > 0 && containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleDeleteSelected}
                          >
                            <i className="bi bi-trash3 me-2"></i>
                            Delete Selected ({selectedBlobs.size})
                          </Button>
                        )}
                        {containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                          <>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setShowCreateFolderModal(true)}
                            >
                              <i className="bi bi-folder-plus me-2"></i>
                              New Folder
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={triggerFileInput}
                              disabled={uploading}
                              className="upload-btn-header"
                            >
                              <i className="bi bi-cloud-upload me-2"></i>
                              Upload Files
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={triggerFolderInput}
                              disabled={uploading}
                              title="Upload an entire folder (keeps its structure)"
                            >
                              <i className="bi bi-folder-symlink me-2"></i>
                              Upload Folder
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Header>
                    
                    {/* Breadcrumb Navigation */}
                    {(currentFolderPath || breadcrumbPaths.length > 0) && (
                      <div className="px-3 pt-3 pb-2">
                        <Breadcrumb>
                          <Breadcrumb.Item
                            onClick={() => handleBreadcrumbClick('')}
                            style={{ cursor: 'pointer' }}
                            active={breadcrumbPaths.length === 0}
                          >
                            <i className="bi bi-house-door me-1"></i>
                            {selectedContainer}
                          </Breadcrumb.Item>
                          {breadcrumbPaths.map((pathSegment, index) => (
                            <Breadcrumb.Item
                              key={index}
                              onClick={() => handleBreadcrumbClick(getBreadcrumbPath(index))}
                              style={{ cursor: 'pointer' }}
                              active={index === breadcrumbPaths.length - 1}
                            >
                              {pathSegment}
                            </Breadcrumb.Item>
                          ))}
                        </Breadcrumb>
                      </div>
                    )}
                    
                    {/* Search Bar */}
                    {blobs.length > 0 && (
                      <div className="px-3 pt-3 pb-2">
                        <Form.Group className="mb-0">
                          <div className="position-relative">
                            <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 10 }}></i>
                            <Form.Control
                              type="text"
                              placeholder="Search files by name..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{ paddingLeft: '38px', paddingRight: searchTerm ? '38px' : '12px' }}
                            />
                            {searchTerm && (
                              <Button
                                variant="link"
                                size="sm"
                                className="position-absolute"
                                style={{ right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '4px 8px', color: '#6c757d' }}
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                              >
                                <i className="bi bi-x-circle-fill"></i>
                              </Button>
                            )}
                          </div>
                          {searchTerm && (
                            <small className="text-muted mt-1 d-block">
                              Found {filteredFolders.length + filteredFiles.length} of {blobs.length} item{blobs.length !== 1 ? 's' : ''}
                            </small>
                          )}
                        </Form.Group>
                      </div>
                    )}
                    
                    <Card.Body
                      className={`files-drop-zone ${isDragging ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {isDragging && containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                        <div className="drag-overlay">
                          <i className="bi bi-cloud-upload" style={{ fontSize: '3rem' }}></i>
                          <p className="mt-3 mb-0">Drop files to upload</p>
                        </div>
                      )}
                      {loadingBlobs ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" size="sm" />
                          <p className="text-muted mt-2">Loading files...</p>
                        </div>
                      ) : blobs.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-inbox" style={{ fontSize: '3.5rem', color: '#ccc' }}></i>
                          <p className="text-muted mt-3 mb-3">No files in this shared folder</p>
                          {containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={triggerFileInput}
                              disabled={uploading}
                            >
                              <i className="bi bi-cloud-upload me-2"></i>
                              Upload your first file
                            </Button>
                          )}
                        </div>
                      ) : (filteredFolders.length === 0 && filteredFiles.length === 0) ? (
                        <div className="text-center py-5">
                          <i className="bi bi-search" style={{ fontSize: '3.5rem', color: '#ccc' }}></i>
                          <p className="text-muted mt-3 mb-3">No items match your search "{searchTerm}"</p>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setSearchTerm('')}
                          >
                            <i className="bi bi-x-circle me-2"></i>
                            Clear Search
                          </Button>
                        </div>
                      ) : (
                        <Table responsive hover className="user-dashboard-table">
                          <thead>
                            <tr>
                              <th style={{ width: '30px' }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={filteredFiles.length > 0 && filteredFiles.every(file => {
                                    const path = file.path || file.name;
                                    return selectedBlobs.has(path);
                                  })}
                                  onChange={handleSelectAllUpdated}
                                  title="Select all files"
                                />
                              </th>
                              <th style={{ width: '35%', maxWidth: '400px', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                                Name <i className={`bi ${sortIcon('name')} ms-1 small`}></i>
                              </th>
                              <th style={{ width: '10%', minWidth: '80px', cursor: 'pointer' }} onClick={() => toggleSort('size')}>
                                Size <i className={`bi ${sortIcon('size')} ms-1 small`}></i>
                              </th>
                              <th style={{ width: '12%', minWidth: '100px', cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                                Type <i className={`bi ${sortIcon('type')} ms-1 small`}></i>
                              </th>
                              <th style={{ width: '18%', minWidth: '150px', cursor: 'pointer' }} onClick={() => toggleSort('modified')}>
                                Modified <i className={`bi ${sortIcon('modified')} ms-1 small`}></i>
                              </th>
                              <th className="text-center" style={{ width: '20%', minWidth: '120px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Folders first */}
                            {filteredFolders.map((folder) => (
                              <tr key={folder.path || folder.name} style={{ cursor: 'pointer' }} onClick={() => handleViewFile(folder)}>
                                <td onClick={(e) => e.stopPropagation()}>
                                  {/* No checkbox for folders */}
                                </td>
                                <td style={{ maxWidth: '400px' }}>
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-folder-fill me-2 text-warning" style={{ flexShrink: 0 }}></i>
                                    <span className="file-name-truncate" title={folder.name}>
                                      <strong>{folder.name}</strong>
                                    </span>
                                  </div>
                                </td>
                                <td>-</td>
                                <td>
                                  <Badge bg="warning" text="dark">FOLDER</Badge>
                                </td>
                                <td>—</td>
                                <td>
                                  <div className="d-flex justify-content-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    {containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                                      <Button
                                        variant="light"
                                        size="sm"
                                        className="action-btn-icon action-btn-delete"
                                        onClick={() => handleDelete(folder)}
                                        title="Delete folder"
                                      >
                                        <i className="bi bi-trash3"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {/* Files */}
                            {sortedFiles.map((file) => {
                              const filePath = file.path || file.name;
                              const isSelected = selectedBlobs.has(filePath);
                              return (
                                <tr key={filePath}>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleToggleSelect(filePath)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td style={{ maxWidth: '400px' }}>
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-file-earmark-text me-2 text-muted" style={{ flexShrink: 0 }}></i>
                                      <span className="file-name-truncate" title={file.name}>{file.name}</span>
                                    </div>
                                  </td>
                                <td>{formatFileSize(file.contentLength)}</td>
                                <td>
                                  <span className="file-type-badge">{getShortFileType(file.contentType)}</span>
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(file.lastModified)}</td>
                                <td>
                                  <div className="d-flex justify-content-center gap-1">
                                    <Button
                                      variant="light"
                                      size="sm"
                                      className="action-btn-icon"
                                      onClick={() => handleViewFile(file)}
                                      title="Preview file"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </Button>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      className="action-btn-icon"
                                      onClick={() => handleOpenInNewTab(file)}
                                      title="Open in new tab"
                                    >
                                      <i className="bi bi-box-arrow-up-right"></i>
                                    </Button>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      className="action-btn-icon"
                                      onClick={() => handleDownload(file)}
                                      title="Download file"
                                    >
                                      <i className="bi bi-download"></i>
                                    </Button>
                                    {containers.find(c => c.name === selectedContainer)?.permissions.includes('write') && (
                                      <Button
                                        variant="light"
                                        size="sm"
                                        className="action-btn-icon action-btn-delete"
                                        onClick={() => handleDelete(file)}
                                        title="Delete file"
                                      >
                                        <i className="bi bi-trash3"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </>
              ) : (
                <Card className="text-center py-5">
                  <Card.Body>
                    <i className="bi bi-arrow-left" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <h5 className="mt-3">Select a Shared Folder</h5>
                    <p className="text-muted">Choose a shared folder from the list to view and upload files</p>
                  </Card.Body>
                </Card>
              )}
            </div>
          </div>
        )}
      </Container>

      {/* Footer */}
      <footer className="bg-light py-3">
        <Container>
          <div className="text-center text-muted">
            &copy; {new Date().getFullYear()} QAI Secure File Transfer Manager
          </div>
        </Container>
      </footer>

      {/* Create Shared Folder (container) Modal */}
      <Modal show={showCreateContainerModal} onHide={() => {
        setShowCreateContainerModal(false);
        setNewContainerName('');
        setCreateContainerError(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>New Shared Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createContainerError && (
            <Alert variant="danger" className="py-2">{createContainerError}</Alert>
          )}
          <Form.Group>
            <Form.Label>Folder Name</Form.Label>
            <Form.Control
              type="text"
              autoFocus
              value={newContainerName}
              onChange={(e) => setNewContainerName(e.target.value)}
              placeholder="e.g. acme-claims"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newContainerName.trim()) {
                  handleCreateContainer();
                }
              }}
            />
            <Form.Text className="text-muted">
              3–63 characters, lowercase letters, numbers and hyphens. This creates a
              new top-level Shared Folder you'll own.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateContainerModal(false);
            setNewContainerName('');
            setCreateContainerError(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateContainer}
            disabled={!newContainerName.trim() || creatingContainer}
          >
            {creatingContainer ? 'Creating…' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Folder Modal */}
      <Modal show={showCreateFolderModal} onHide={() => {
        setShowCreateFolderModal(false);
        setNewFolderName('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Folder Name</Form.Label>
            <Form.Control 
              type="text" 
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newFolderName.trim()) {
                  handleCreateFolder();
                }
              }}
            />
            <Form.Text className="text-muted">
              {currentFolderPath && (
                <>Folder will be created in: <strong>{currentFolderPath}</strong></>
              )}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateFolderModal(false);
            setNewFolderName('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || creatingFolder}
          >
            {creatingFolder ? 'Creating...' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Viewer Modal */}
      {selectedBlob && selectedContainer && !selectedBlob.isFolder && (
        <UserFileViewer
          show={showFileViewer}
          onHide={() => setShowFileViewer(false)}
          containerName={selectedContainer}
          fileName={selectedBlob.path || selectedBlob.name}
          contentType={selectedBlob.contentType}
          fileSize={selectedBlob.contentLength}
        />
      )}
    </div>
  );
};

export default UserDashboard;

